import { useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Hook for polling an async project until status is completed or failed.
 *
 * Usage:
 *   const { project, polling, error, start, reset } = useProjectPolling();
 *   start(project_id);  // begins polling every 5s, stops on completed/failed
 */
export function useProjectPolling({ intervalMs = 5000, maxMs = 15 * 60 * 1000 } = {}) {
  const [project, setProject] = useState(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const idRef = useRef(null);

  const stop = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  };

  const reset = () => {
    stop();
    setProject(null);
    setError(null);
    startedAtRef.current = null;
    idRef.current = null;
  };

  const tick = async () => {
    const projectId = idRef.current;
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      if (data.status === "completed" || data.status === "failed") {
        stop();
        if (data.status === "failed") {
          setError(data.error || "Génération échouée");
        }
        return;
      }
      // Still generating - check timeout
      if (Date.now() - startedAtRef.current > maxMs) {
        stop();
        setError("Délai d'attente dépassé (15 min)");
        return;
      }
      timerRef.current = setTimeout(tick, intervalMs);
    } catch (e) {
      stop();
      setError(e?.response?.data?.detail || "Erreur de polling");
    }
  };

  const start = (projectId) => {
    reset();
    idRef.current = projectId;
    startedAtRef.current = Date.now();
    setPolling(true);
    // Kick first poll quickly (1s) then space out
    timerRef.current = setTimeout(tick, 1000);
  };

  useEffect(() => () => stop(), []);

  return { project, polling, error, start, reset };
}
